import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import i18n from '@/i18n';
import { session } from './session';
import { ApiError, toApiError } from './errors';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/uruanna';

export const http = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

/** Paths that must never carry an Authorization header or trigger a refresh. */
const PUBLIC_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/refresh',
  '/api/v1/auth/otp/send',
  '/api/v1/auth/otp/verify',
  '/api/v1/auth/password/forgot',
  '/api/v1/auth/password/reset',
  // Accepting an invitation: unauthenticated by definition — the account being
  // claimed cannot sign in yet, and any token lying around belongs to someone
  // else on a shared machine.
  '/api/v1/auth/invite/verify',
  '/api/v1/auth/invite/accept',
];

function isPublic(url = ''): boolean {
  return PUBLIC_PATHS.some((p) => url.includes(p));
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.headers.set('Accept-Language', i18n.language || 'en');
  /*
   * The instance defaults to JSON, which is wrong for an upload: multipart
   * needs a boundary parameter that only the browser can generate, and it will
   * not add one while a Content-Type is already set. Clearing it here lets the
   * browser fill in `multipart/form-data; boundary=…` itself.
   */
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  if (!isPublic(config.url) && session.accessToken) {
    config.headers.set('Authorization', `Bearer ${session.accessToken}`);
  }
  return config;
});

/**
 * One refresh at a time. Concurrent 401s queue on the same promise so a burst
 * of requests never burns several refresh tokens — the backend revokes every
 * session when it sees a replayed one.
 */
let refreshing: Promise<string> | null = null;
let onSessionLost: (() => void) | null = null;
let onOrganizationNotVerified: (() => void) | null = null;

export function setSessionLostHandler(handler: () => void) {
  onSessionLost = handler;
}

/**
 * Called when any request answers ORGANIZATION_NOT_VERIFIED. The caller is not
 * broken and has not lost anything — their organization simply has not been
 * approved yet — so the app routes them to the registration wizard rather than
 * showing a generic failure.
 */
export function setOrganizationNotVerifiedHandler(handler: () => void) {
  onOrganizationNotVerified = handler;
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = session.refreshToken;
  if (!refreshToken) throw new ApiError({ errorCode: 'REFRESH_TOKEN_INVALID', statusCode: 401 });
  const { data } = await axios.post(`${BASE_URL}/api/v1/auth/refresh`, { refreshToken });
  session.set(data);
  return data.accessToken as string;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const apiError = toApiError(error);
    const expired = apiError.code === 'TOKEN_EXPIRED' || apiError.code === 'TOKEN_INVALID' || apiError.status === 401;

    if (expired && original && !original._retried && !isPublic(original.url)) {
      original._retried = true;
      try {
        refreshing ??= refreshAccessToken().finally(() => {
          refreshing = null;
        });
        const token = await refreshing;
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return http.request(original);
      } catch {
        session.clear();
        onSessionLost?.();
      }
    }
    if (apiError.code === 'ORGANIZATION_NOT_VERIFIED') {
      onOrganizationNotVerified?.();
      // Swallowed deliberately: the redirect is the answer, and a toast on top
      // of it would only tell the user something they are already being shown.
      return new Promise(() => {});
    }

    return Promise.reject(apiError);
  },
);

/** Thin helpers so call sites stay free of axios types. */
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => http.get<T>(url, config).then((r) => r.data),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    http.post<T>(url, body, config).then((r) => r.data),
  put: <T>(url: string, body?: unknown) => http.put<T>(url, body).then((r) => r.data),
  patch: <T>(url: string, body?: unknown) => http.patch<T>(url, body).then((r) => r.data),
  delete: <T>(url: string) => http.delete<T>(url).then((r) => r.data),
};
